import { useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import AutocompleteInput from "@/components/shared/AutocompleteInput";
import DrugSearchInput from "@/components/shared/DrugSearchInput";
import FormTable, { MedicineTableColumns } from "@/components/shared/FormTable";
import { useAuth } from "@/context/AuthContext";
import { DrugSearchResult, recordDrugUsage } from "@/lib/api";
import {
    getDosageOptions,
    WHEN_OPTIONS,
    DURATION_OPTIONS,
    ROUTE_OPTIONS,
    Drug,
} from "@/lib/constants";
import { Patient } from "@/types";
import SectionPropertiesProps from "@/components/shared/SectionProperties.tsx";


// Per-row shape stored in RHF. Includes drug meta needed for dosage options.
const DEFAULT_ROW = {
    drug_id:      null as number | null,
    product:      "",
    brand_name:   "",
    generic_name: "",
    // Drug meta retained for smart dosage options — not submitted to API
    _drug_form:   "",
    _drug_brand:  "",
    // Prescription fields
    dosage:       "",
    taken_when:   "",
    route:        "",
    duration:     "",
    quantity:     "",
    notes:        "",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function rowToDrug(form: string, brand: string): Drug {
    return {
        form:       form  || "tablet",
        brand_name: brand || "",
    };
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function MedicineListForm({ patient }: SectionPropertiesProps) {
    const {
        control,
        register,
        getValues,
        setValue,
        watch,
        trigger,
        formState: { errors },
    } = useFormContext();

    const { fields, append, remove } = useFieldArray({ control, name: "medicines" });
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const watched = watch("medicines") || [];
    const [initialized, setInitialized] = useState(false);
    const { currentUser } = useAuth();

    // Paediatric flag — use age if available, else derive from date_of_birth
    const isPaediatric = (() => {
        if (patient?.age !== undefined) return patient.age < 18;
        if (patient?.date_of_birth) {
            const birth = new Date(patient.date_of_birth);
            if (!isNaN(birth.getTime())) {
                const ageYears =
                    (new Date().getFullYear() - birth.getFullYear());
                return ageYears < 18;
            }
        }
        return false;
    })();

    // Initialize with one empty row if none exist
    useEffect(() => {
        if (!initialized && fields.length === 0) {
            append(DEFAULT_ROW);
            setInitialized(true);
        }
    }, [fields.length, append, initialized]);

    // ── Drug selection ──────────────────────────────────────────────────────────

    const handleDrugSelect = (index: number, drug: DrugSearchResult) => {
        setValue(`medicines.${index}.drug_id`,      drug.id,                     { shouldDirty: false });
        setValue(`medicines.${index}.brand_name`,   drug.brand_name,             { shouldDirty: true });
        setValue(`medicines.${index}.product`,      drug.brand_name,             { shouldDirty: true });
        setValue(`medicines.${index}.generic_name`, drug.composition_raw ?? "",  { shouldDirty: false });

        // Retain drug meta for dosage option resolution (not submitted to API)
        setValue(`medicines.${index}._drug_form`,  drug.form ?? "tablet", { shouldDirty: false });
        setValue(`medicines.${index}._drug_brand`, drug.brand_name ?? "",  { shouldDirty: false });

        // Auto-fill route from drug catalog (e.g. oral, topical, injection)
        setValue(`medicines.${index}.route`, drug.route ?? "oral", { shouldDirty: false });

        const dosage    = drug.custom_dosage     ?? drug.default_dosage;
        const takenWhen = drug.custom_taken_when ?? drug.default_taken_when;
        const duration  = drug.custom_duration   ?? drug.default_duration;
        if (dosage)             setValue(`medicines.${index}.dosage`,     dosage,             { shouldDirty: false });
        if (takenWhen)          setValue(`medicines.${index}.taken_when`, takenWhen,          { shouldDirty: false });
        if (duration)           setValue(`medicines.${index}.duration`,   duration,           { shouldDirty: false });
        if (drug.default_notes) setValue(`medicines.${index}.notes`,      drug.default_notes, { shouldDirty: false });

        if (currentUser?.doctor_id && currentUser?.tenant_id) {
            recordDrugUsage(drug.id, currentUser.doctor_id, currentUser.tenant_id).catch(() => {});
        }
    };

    // ── Dosage options for a row ────────────────────────────────────────────────

    const getRowDosageOptions = (index: number): string[] => {
        const form  = getValues(`medicines.${index}._drug_form`)  || "";
        const brand = getValues(`medicines.${index}._drug_brand`) || "";
        return getDosageOptions(rowToDrug(form, brand), isPaediatric);
    };

    // ── Auto-quantity calculation ───────────────────────────────────────────────

    const handleAutoQuantity = (index: number) => {
        setTimeout(() => {
            // Only auto-calculate for tablets and capsules.
            // Syrups → bottles, Injections → amps/vials, Inhalers → units.
            // These are not calculable from dosage×duration — doctor fills manually.
            const drugForm = (getValues(`medicines.${index}._drug_form`) || "tablet").toLowerCase();
            const calculableForms = ["tablet", "capsule"];
            if (!calculableForms.includes(drugForm)) return;

            const dosage   = getValues(`medicines.${index}.dosage`)?.trim()   || "";
            const duration = getValues(`medicines.${index}.duration`)?.trim() || "";

            // Strip unit suffixes before parsing — handles "5ml-0-5ml", "1 puff-0-1 puff", "½-0-½"
            const parsePart = (part: string): number => {
                return parseFloat(
                    part
                        .replace(/ml|puffs?|amp|tabs?/gi, "")
                        .replace("½", "0.5")
                        .replace("¼", "0.25")
                        .trim()
                ) || 0;
            };
            const dosageParts = dosage.split("-").map(parsePart);
            const dailyDoses  = dosageParts.reduce((sum: number, d: number) => sum + d, 0);

            let durationDays = 0;
            const dl = duration.toLowerCase();

            if (dl.includes("day")) {
                const m = duration.match(/(\d+)\s*days?/i);
                durationDays = m ? parseInt(m[1]) : 0;
            } else if (dl.includes("week")) {
                const m = duration.match(/(\d+)\s*weeks?/i);
                durationDays = m ? parseInt(m[1]) * 7 : 0;
            } else if (dl.includes("month")) {
                const m = duration.match(/(\d+)\s*months?/i);
                durationDays = m ? parseInt(m[1]) * 30 : 0;
            } else if (dl.includes("continue") || dl.includes("long term")) {
                durationDays = 30;
            } else if (dl.includes("till review") || dl.includes("till next visit")) {
                durationDays = 15;
            } else if (dl.includes("complete course")) {
                durationDays = 7;
            } else if (dl.includes("as directed")) {
                durationDays = 10;
            } else {
                const m = duration.match(/^\d+/);
                durationDays = m ? parseInt(m[0]) : 0;
            }

            if (dailyDoses > 0 && durationDays > 0) {
                const total = Math.ceil(dailyDoses * durationDays);
                setValue(`medicines.${index}.quantity`, total.toString(), {
                    shouldValidate: true,
                    shouldDirty:    true,
                    shouldTouch:    true,
                });
                trigger(`medicines.${index}.quantity`);
            }
        }, 100);
    };

    // ── Focus helpers ───────────────────────────────────────────────────────────

    const focusNextField = (index: number, fieldOffset: number) => {
        const nextIndex = index * 7 + fieldOffset;
        if (inputRefs.current[nextIndex]) {
            setTimeout(() => inputRefs.current[nextIndex]?.focus(), 50);
        }
    };

    // ── Cell renderer ───────────────────────────────────────────────────────────

    const renderCell = (field: any, columnKey: string, index: number) => {
        switch (columnKey) {

            case "medicine": {
                const genericName = watch(`medicines.${index}.generic_name`) ?? "";
                return (
                    <DrugSearchInput
                        value={getValues(`medicines.${index}.product`) ?? ""}
                        onChange={(drug) => handleDrugSelect(index, drug)}
                        onCustomInput={(val) => {
                            setValue(`medicines.${index}.product`,      val,  { shouldDirty: false, shouldTouch: false, shouldValidate: false });
                            setValue(`medicines.${index}.drug_id`,      null, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
                            setValue(`medicines.${index}._drug_form`,   "",   { shouldDirty: false });
                            setValue(`medicines.${index}._drug_brand`,  "",   { shouldDirty: false });
                            setValue(`medicines.${index}.generic_name`, "",   { shouldDirty: false });
                            setValue(`medicines.${index}.route`,        "",   { shouldDirty: false });
                        }}
                        placeholder="Search medicine..."
                        onEnterNext={() => focusNextField(index, 1)}
                        subtitle={genericName || undefined}
                        className={cn(
                            errors.medicines?.[index]?.product && "ring-1 ring-destructive/50 rounded",
                        )}
                        ref={(el) => { inputRefs.current[index * 7] = el; }}
                    />
                );
            }

            case "dosage": {
                const options = getRowDosageOptions(index);
                return (
                    <AutocompleteInput
                        value={getValues(`medicines.${index}.dosage`) ?? ""}
                        onChange={(val) => {
                            setValue(`medicines.${index}.dosage`, val, { shouldDirty: true });
                            handleAutoQuantity(index);
                        }}
                        options={options}
                        placeholder={options[0] ?? "Dosage"}
                        onEnterNext={() => focusNextField(index, 2)}
                        className={cn(
                            errors.medicines?.[index]?.dosage && "ring-1 ring-destructive/50 rounded",
                        )}
                        showAllOnFocus={true}
                        dropdownMinWidth={120}
                        ref={(el) => { inputRefs.current[index * 7 + 1] = el; }}
                    />
                );
            }

            case "taken_when":
                return (
                    <AutocompleteInput
                        value={getValues(`medicines.${index}.taken_when`) ?? ""}
                        onChange={(val) => setValue(`medicines.${index}.taken_when`, val, { shouldDirty: true })}
                        options={WHEN_OPTIONS}
                        placeholder="When"
                        onEnterNext={() => focusNextField(index, 3)}
                        className={cn(
                            errors.medicines?.[index]?.taken_when && "ring-1 ring-destructive/50 rounded",
                        )}
                        showAllOnFocus={true}
                        dropdownMinWidth={200}
                        ref={(el) => { inputRefs.current[index * 7 + 2] = el; }}
                    />
                );

            case "route":
                return (
                    <AutocompleteInput
                        value={getValues(`medicines.${index}.route`) ?? ""}
                        onChange={(val) => setValue(`medicines.${index}.route`, val, { shouldDirty: true })}
                        options={ROUTE_OPTIONS}
                        placeholder="Oral"
                        onEnterNext={() => focusNextField(index, 4)}
                        showAllOnFocus={true}
                        dropdownMinWidth={150}
                        ref={(el) => { inputRefs.current[index * 7 + 3] = el; }}
                    />
                );

            case "duration":
                return (
                    <AutocompleteInput
                        value={getValues(`medicines.${index}.duration`) ?? ""}
                        onChange={(val) => {
                            setValue(`medicines.${index}.duration`, val, { shouldDirty: true });
                            handleAutoQuantity(index);
                        }}
                        options={DURATION_OPTIONS}
                        placeholder="5 days"
                        onEnterNext={() => focusNextField(index, 5)}
                        className={cn(
                            errors.medicines?.[index]?.duration && "ring-1 ring-destructive/50 rounded",
                        )}
                        showAllOnFocus={true}
                        dropdownMinWidth={200}
                        ref={(el) => { inputRefs.current[index * 7 + 4] = el; }}
                    />
                );

            case "quantity": {
                const rawQuantity = watch(`medicines.${index}.quantity`);
                const hasProduct = !!(watch(`medicines.${index}.product`)?.trim());
                const quantityValue =
                    !hasProduct || rawQuantity == null || rawQuantity === "" || rawQuantity === "0" || rawQuantity === 0
                        ? ""
                        : String(rawQuantity);
                return (
                    <div className="flex items-center justify-center gap-1">
                        <Input
                            type="number"
                            {...register(`medicines.${index}.quantity`)}
                            value={quantityValue}
                            min="0"
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
                                    setValue(`medicines.${index}.quantity`, value, { shouldValidate: true, shouldDirty: true });
                                }
                            }}
                            className={cn(
                                "w-full text-center text-xs text-foreground bg-transparent shadow-none",
                                "border-0 outline-none ring-0",
                                "placeholder:text-muted-foreground/40 transition-colors duration-100",
                                "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                errors.medicines?.[index]?.quantity,
                            )}
                            placeholder=""
                            ref={(el) => { inputRefs.current[index * 7 + 5] = el; }}
                            onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    focusNextField(index, 6);
                                }
                            }}
                        />
                    </div>
                );
            }

            case "notes":
                return (
                    <Input
                        {...register(`medicines.${index}.notes`)}
                        placeholder="Notes"
                        className={cn(
                            "w-full text-xs rounded-none border-0 outline-none ring-0 shadow-none px-1",
                            "bg-transparent transition-colors duration-150",
                            "placeholder:text-muted-foreground/40",
                            errors.medicines?.[index]?.notes,
                        )}
                        ref={(el) => { inputRefs.current[index * 7 + 6] = el; }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                const nextRow = (index + 1) * 7;
                                if (inputRefs.current[nextRow]) {
                                    inputRefs.current[nextRow]?.focus();
                                } else {
                                    append(DEFAULT_ROW);
                                    setTimeout(() => inputRefs.current[nextRow]?.focus(), 50);
                                }
                            }
                        }}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <FormTable
            title="Medicine"
            columns={MedicineTableColumns}
            fields={fields}
            watched={watched}
            errors={errors}
            renderCell={renderCell}
            onAddRow={() => append(DEFAULT_ROW)}
            onRemoveRow={(index) => remove(index)}
            canRemoveRow={() => fields.length > 1}
            addButtonText="Add Medicine"
            showRowNumbers={true}
            autoAddRow={true}
            primaryField="product"
            rowSize="normal"
        />
    );
}